/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {PureComponent} from "react";
import PropTypes from "prop-types";
import humanizeString from "string-humanize";
import {get, isBoolean} from "lodash";
import {Badge, Card} from "reactstrap";
import {FaTrash} from "react-icons/fa";
import Toggle from "react-toggle";
import Tooltip from "../Tooltip/Tooltip";
import ProtectedContent from "../protectedContent";
import Popover from "../Popover";
import PopoverItem from "../Popover/PopoverItem";
import "./tableStyles.scss";

class Table extends PureComponent {
  static propTypes = {
    /** List of headers to display on table.
     *  They need to match the row object keys */
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    /** Data which is need to be displayed */
    data: PropTypes.array.isRequired,
    /** Name for the table which will used in giving id to table data */
    tableName: PropTypes.string,
    /** This prop will determine wether row is clickable */
    rowClickable: PropTypes.bool,
    /** Callback function which will be executed when clicked on row */
    onSelectRow: PropTypes.func,
    /** Wether to display action column on table */
    withActions: PropTypes.bool,
    /** List of actions which will be passed to action popover */
    actionsList: PropTypes.array,
    /** Permissions needed for actions */
    permsForActions: PropTypes.array,
    /** Function which will be executed when clicked on action */
    onSelectAction: PropTypes.func,
    /** If this prop is set to true boolean values will be displayed as toggle */
    booleanAsToggle: PropTypes.bool,
    /** id which will be passed as data-cy attribute to toggle component */
    toggleTestId: PropTypes.string,
    /** Callback function which will be executed when clicked on toggle */
    onClickToggle: PropTypes.func,
    /** Key for which custom cell will be rendered.
     *  This key should be one of headers
     */
    customRenderKeys: PropTypes.arrayOf(PropTypes.string),
    /** Function which will be executed for custom cell
     *  This should return react element
     */
    customRenderComponent: PropTypes.func,
  };

  buildReadableHeader(header) {
    let header_list = header.split(".");
    header = header_list[header_list.length - 1];
    return humanizeString(header).replace("Is ", "");
  }

  renderTableHeader() {
    const {
      tableName = "table",
      headers = [],
      withActions = false,
      permsForActions = [],
    } = this.props;

    return (
      <tr className="table-left">
        {headers.map((header) => (
          <th
            key={header}
            className={`roboto-bold table__header ${`${tableName}--${header}`}`}
            data-title={header}
          >
            <span>{this.buildReadableHeader(header)}</span>
          </th>
        ))}
        {withActions && (
          <ProtectedContent perms={permsForActions} shouldHaveAllPermissions={false}>
            <th className="roboto-bold table__header table-center">
              <span>Actions</span>
            </th>
          </ProtectedContent>
        )}
      </tr>
    );
  }

  getBooleanComponent = (keyValue, row, header) => {
    const {
      booleanAsToggle = false,
      onClickToggle = () => {},
      toggleTestId = "toggle",
    } = this.props;
    if (booleanAsToggle) {
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Toggle
            checked={keyValue}
            onChange={() => onClickToggle(row, header, keyValue)}
            data-cy={toggleTestId}
          />
        </div>
      );
    } else {
      return (
        <Badge color="" className="badge-dot ml-1">
          {keyValue ? <i className="bg-success" /> : <i className="bg-warning" />}
        </Badge>
      );
    }
  };

  getActions = (row) => {
    const {filterActionsOn, actionsList = []} = this.props;
    if (filterActionsOn) {
      if (row[filterActionsOn]) {
        return actionsList;
      } else {
        return [{label: "Delete", icon: FaTrash}];
      }
    } else {
      return actionsList;
    }
  };

  renderTableData = (rowData, key) => {
    const {customRenderKeys, customRenderComponent: CustomRenderComponent} = this.props;

    if (customRenderKeys?.includes(key)) {
      return <CustomRenderComponent data={rowData} renderKey={key} />;
    } else if (isBoolean(rowData[key])) {
      return this.getBooleanComponent(get(rowData, key), rowData, key);
    } else if (Array.isArray(get(rowData, key))) {
      return get(rowData, key).join(", ");
    } else {
      return get(rowData, key) ?? "-";
    }
  };

  renderTableBody() {
    const {
      tableName = "table",
      data = [],
      headers = [],
      withActions = false,
      permsForActions = [],
      onSelectAction = () => {},
      rowClickable = false,
      onSelectRow = () => {},
    } = this.props;
    return data.map((eachRow, rowIndex) => {
      return (
        <tr
          key={`row-${eachRow.id}`}
          className={rowIndex % 2 === 0 ? "table__row--active table-left" : "table-left"}
          data-cy={`${tableName}-row-${rowIndex}`}
        >
          {headers.map((key, i) => (
            <React.Fragment key={`row-${eachRow.id}-column-${i}`}>
              <td
                onClick={
                  rowClickable
                    ? () => {
                        onSelectRow(eachRow);
                      }
                    : null
                }
                className={`roboto-normal ${rowClickable ? "cursor-pointer" : ""}`}
                id={`${tableName}-${key}-${rowIndex}${i}`}
                data-cy={`cy_${tableName}`}
              >
                {this.renderTableData(eachRow, key)}
              </td>
              <Tooltip target={`${tableName}-${key}-${rowIndex}${i}`} />
            </React.Fragment>
          ))}

          {withActions && (
            <ProtectedContent perms={permsForActions} shouldHaveAllPermissions={false}>
              <td className="table-center">
                <Popover>
                  {this.getActions(eachRow).map((action, index) => {
                    const {label, icon, perms, condition = ""} = action;
                    if (condition && eachRow[condition] === false) {
                      return null;
                    }

                    return (
                      <PopoverItem
                        key={index}
                        onContentSelection={(action) => onSelectAction(action, eachRow)}
                        label={label}
                        icon={icon}
                        perms={perms}
                      />
                    );
                  })}
                </Popover>
              </td>
            </ProtectedContent>
          )}
        </tr>
      );
    });
  }

  render() {
    return (
      <Card className="shadow">
        <table
          data-cy={`${this.props.tableName}-table`}
          className="table zeus-table table-curved"
        >
          <thead>{this.renderTableHeader()}</thead>
          <tbody>{this.renderTableBody()}</tbody>
        </table>
      </Card>
    );
  }
}

export default Table;
